import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { ApiCustomer } from '@vend/types'
import { FEATURE_CAPTURE_ADDRESS_SELL_SCREEN } from '../../../../../../constants'
import {
  FulfilmentDetail,
  FulfilmentType,
  isDefinedFulfilmentType,
} from '../../../../../../models/fulfilment'
import {
  useCustomerManager,
  useSaleManager,
} from '../../../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../../../state/hooks'
import {
  selectLineItem,
  setFulfilmentDetails,
} from '../../../../../../state/slices/currentSale/currentSaleSlice'
import {
  markSaleCustomerContactInfoAsReviewed,
  selectOpenFulfilmentPopoverLineItemId,
} from '../../../../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../../../../util/feature-cache'
import {
  CurrentView,
  useLineItemFulfillmentContext,
} from '../LineItemFulfillmentContext'
import {
  isContactInfoUpdated,
  trimCustomerInfo,
} from '../LineItemFulfillmentUtils'
import { generateAddressDetailsFromCustomer } from '../../fulfillmentHelper'
import {
  AddressErrorsStatus,
  useLineItemFulfillmentAddressDetails,
} from './useLineItemFulfillmentAddressDetails'

interface useLineItemFulfillmentReviewContactInfoFlowProps {
  onClose: () => void
  onSelectFulfilmentType?: (type: FulfilmentType) => void
}

export const useLineItemFulfillmentReviewContactInfoFlow = ({
  onClose,
}: useLineItemFulfillmentReviewContactInfoFlowProps) => {
  const {
    setCurrentView,
    currentCustomer,
    originalCustomer,
    setIsLoading,
    onSelectFulfilmentType,
    fulfilmentDetails,
    setFulfilmentDetails: setContextFulfilmentDetails,
  } = useLineItemFulfillmentContext()

  const t = useT()
  const saleManager = useSaleManager()
  const customerManager = useCustomerManager()
  const lineItemId = useAppSelector(selectOpenFulfilmentPopoverLineItemId)
  const lineItem = useAppSelector(selectLineItem(lineItemId))
  const dispatch = useAppDispatch()
  const isCaptureAddressSellScreenEnabled = getFeatureFromCache(
    FEATURE_CAPTURE_ADDRESS_SELL_SCREEN
  )

  const {
    shouldFixSaleAddresses,
    status,
  } = useLineItemFulfillmentAddressDetails(fulfilmentDetails)

  const handleContinueFromReviewContactInfoView = async () => {
    const fulfilmentType = lineItem?.fulfilmentType
      ? lineItem?.fulfilmentType
      : FulfilmentType.UNSPECIFIED

    if (currentCustomer && originalCustomer) {
      if (onSelectFulfilmentType) {
        // calling this here in case the fulfilment type is already set from fulfilments tab
        onSelectFulfilmentType(fulfilmentType)
      }
      const isUpdated = isContactInfoUpdated(currentCustomer, originalCustomer)
      if (isUpdated) {
        try {
          trimCustomerInfo(currentCustomer)
          setIsLoading(true)
          const savedCustomer = await customerManager.updateCustomer(
            currentCustomer
          )
          await saleManager.addCustomer(savedCustomer)
          goToNextView()
        } catch (_) {
          ToastNotifications.negative(
            t('Unable to update customer. Try again.')
          )
        } finally {
          updateFulfilmentAddressDetails(fulfilmentDetails, currentCustomer)
          setIsLoading(false)
        }
      } else {
        setIsLoading(true)
        await saleManager.addCustomer(currentCustomer)
        updateFulfilmentAddressDetails(fulfilmentDetails, currentCustomer)
        setIsLoading(false)
        goToNextView()
      }
    }
  }

  const goToNextView = () => {
    if (isDefinedFulfilmentType(lineItem?.fulfilmentType)) {
      const isAddressEmpty =
        status === AddressErrorsStatus.EMPTYSHIPPING_EMPTYBILLING
      if (
        isCaptureAddressSellScreenEnabled &&
        lineItem?.fulfilmentType === FulfilmentType.DELIVERY &&
        currentCustomer &&
        (shouldFixSaleAddresses || isAddressEmpty)
      ) {
        if (isAddressEmpty) {
          dispatch(
            setFulfilmentDetails([
              { ...fulfilmentDetails, shouldAddNewAddress: true },
            ])
          )
          onClose()
        } else {
          setCurrentView(CurrentView.FIX_SALE_ADDRESSES)
        }
      } else onClose()
    } else {
      setCurrentView(CurrentView.FULFILMENT_METHOD)
    }

    dispatch(markSaleCustomerContactInfoAsReviewed())
  }

  const updateFulfilmentAddressDetails = (
    fulfilmentDetails: FulfilmentDetail,
    currentCustomer: ApiCustomer
  ) => {
    if (
      isCaptureAddressSellScreenEnabled &&
      fulfilmentDetails.type === FulfilmentType.DELIVERY
    ) {
      const newFulfilmentDetails = {
        ...fulfilmentDetails,
        ...generateAddressDetailsFromCustomer(currentCustomer),
      }

      // Update both Redux state and Context state
      const updatedFulfilmentDetail = {
        ...fulfilmentDetails,
        billingAddress: newFulfilmentDetails.billingAddress,
        shippingAddress: newFulfilmentDetails.shippingAddress,
      }

      // Update Redux state
      dispatch(setFulfilmentDetails([updatedFulfilmentDetail]))

      // Update Context state so components reading from context get fresh data
      setContextFulfilmentDetails(updatedFulfilmentDetail)

      return saleManager.updateFulfillmentDetails([newFulfilmentDetails])
    }
  }

  return {
    handleContinueFromReviewContactInfoView,
  }
}
